import {AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2, inject} from '@angular/core';
import {LanguageService} from '../services/language.service';

@Directive({
  selector: '[appNgSrcImageError]',
  standalone: true
})
export class NgSrcImageErrorDirective implements AfterViewInit, OnDestroy {
  private el: ElementRef<HTMLImageElement> = inject(ElementRef<HTMLImageElement>);
  private renderer: Renderer2 = inject(Renderer2);
  private languageService: LanguageService = inject(LanguageService);

  @Input() public imageLoadErrorTextKey: string = 'IMAGELOADERROR';

  private overlay: HTMLElement | null = null;
  private overlayText: HTMLElement | null = null;
  private parentElement: HTMLElement | null = null;
  private previousParentPosition: string | null = null;
  private previousImageVisibility: string | null = null;
  private currentNgSrc: string | null | undefined = undefined;
  private mutationObserver: MutationObserver | null = null;
  private positionFrame: number | null = null;
  private readonly minimumUsefulOverlayWidth: number = 96;
  private readonly minimumUsefulOverlayHeight: number = 48;
  private readonly updateOverlayPosition: () => void = () => {
    this.positionOverlay();
  };

  @Input()
  public set ngSrc(value: string | null | undefined) {
    if (this.currentNgSrc !== value) {
      this.currentNgSrc = value;
      this.removeOverlay();
    }
  }

  public ngAfterViewInit(): void {
    this.mutationObserver = new MutationObserver(() => {
      this.removeOverlay();
    });
    this.mutationObserver.observe(this.el.nativeElement, {
      attributeFilter: ['src', 'srcset'],
      attributes: true
    });
  }

  @HostListener('error')
  public onImageError(): void {
    this.showOverlay();
  }

  @HostListener('load')
  public onImageLoad(): void {
    this.removeOverlay();
  }

  public ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.removeOverlay();
  }

  private showOverlay(): void {
    const image = this.el.nativeElement;
    const parent = image.parentElement;

    if (!parent) {
      return;
    }

    this.parentElement = parent;
    this.ensurePositionedParent(parent);
    this.hideImage(image);

    if (!this.overlay) {
      this.overlay = this.createOverlay();
      this.renderer.appendChild(parent, this.overlay);
      window.addEventListener('resize', this.updateOverlayPosition);
    }

    this.updateText();
    this.positionOverlay();
    this.scheduleOverlayPositionUpdate();
  }

  private createOverlay(): HTMLElement {
    const overlay = this.renderer.createElement('div') as HTMLElement;
    const icon = this.renderer.createElement('span') as HTMLElement;
    const text = this.renderer.createElement('span') as HTMLElement;

    this.renderer.addClass(overlay, 'ng-src-image-error-overlay');
    this.renderer.addClass(icon, 'mts-icon');
    this.renderer.addClass(icon, 'ng-src-image-error-icon');
    this.renderer.addClass(text, 'ng-src-image-error-text');

    this.renderer.setAttribute(overlay, 'role', 'status');
    this.renderer.setStyle(overlay, 'position', 'absolute');
    this.renderer.setStyle(overlay, 'z-index', '10');
    this.renderer.setStyle(overlay, 'display', 'flex');
    this.renderer.setStyle(overlay, 'flex-direction', 'column');
    this.renderer.setStyle(overlay, 'align-items', 'center');
    this.renderer.setStyle(overlay, 'justify-content', 'center');
    this.renderer.setStyle(overlay, 'gap', '0.25rem');
    this.renderer.setStyle(overlay, 'padding', '0.5rem');
    this.renderer.setStyle(overlay, 'background', 'var(--bs-body-bg, #fff)');
    this.renderer.setStyle(overlay, 'color', 'var(--bs-secondary-color, #6c757d)');
    this.renderer.setStyle(overlay, 'border-radius', '0.25rem');
    this.renderer.setStyle(overlay, 'text-align', 'center');
    this.renderer.setStyle(overlay, 'line-height', '1.2');
    this.renderer.setStyle(overlay, 'pointer-events', 'none');
    this.renderer.setStyle(overlay, 'box-sizing', 'border-box');

    this.renderer.setStyle(icon, 'font-size', '2rem');
    this.renderer.setStyle(icon, 'line-height', '1');
    this.renderer.setStyle(text, 'font-size', '0.875rem');
    this.renderer.setStyle(text, 'font-weight', '600');
    this.renderer.setStyle(text, 'overflow-wrap', 'anywhere');

    this.renderer.setProperty(icon, 'textContent', 'broken_image');
    this.renderer.appendChild(overlay, icon);
    this.renderer.appendChild(overlay, text);

    this.overlayText = text;
    return overlay;
  }

  private ensurePositionedParent(parent: HTMLElement): void {
    const position = window.getComputedStyle(parent).position;

    if (position === 'static') {
      this.previousParentPosition = parent.style.position;
      this.renderer.setStyle(parent, 'position', 'relative');
    }
  }

  private hideImage(image: HTMLImageElement): void {
    if (this.previousImageVisibility === null) {
      this.previousImageVisibility = image.style.visibility;
    }

    this.renderer.setStyle(image, 'visibility', 'hidden');
  }

  private positionOverlay(): void {
    if (!this.overlay || !this.parentElement) {
      return;
    }

    const frame = this.getOverlayFrame();
    this.renderer.setStyle(this.overlay, 'top', `${frame.top}px`);
    this.renderer.setStyle(this.overlay, 'left', `${frame.left}px`);
    this.renderer.setStyle(this.overlay, 'width', `${frame.width}px`);
    this.renderer.setStyle(this.overlay, 'height', `${frame.height}px`);
  }

  private getOverlayFrame(): {top: number; left: number; width: number; height: number} {
    const image = this.el.nativeElement;
    const parent = this.parentElement!;
    const imageWidth = image.offsetWidth;
    const imageHeight = image.offsetHeight;
    const useParentFrame = imageWidth < this.minimumUsefulOverlayWidth || imageHeight < this.minimumUsefulOverlayHeight;

    if (useParentFrame) {
      return {
        top: 0,
        left: 0,
        width: Math.max(parent.clientWidth, imageWidth, Number(image.getAttribute('width')) || 0),
        height: Math.max(parent.clientHeight, imageHeight, Number(image.getAttribute('height')) || 0)
      };
    }

    return {
      top: image.offsetTop,
      left: image.offsetLeft,
      width: imageWidth,
      height: imageHeight
    };
  }

  private scheduleOverlayPositionUpdate(): void {
    if (this.positionFrame !== null) {
      cancelAnimationFrame(this.positionFrame);
    }

    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = null;
      this.positionOverlay();
    });
  }

  private updateText(): void {
    if (!this.overlayText) {
      return;
    }

    this.renderer.setProperty(
      this.overlayText,
      'textContent',
      this.languageService.getTranslationByKey(this.imageLoadErrorTextKey)
    );
  }

  private removeOverlay(): void {
    if (this.overlay && this.parentElement) {
      this.renderer.removeChild(this.parentElement, this.overlay);
    }

    if (this.parentElement && this.previousParentPosition !== null) {
      this.renderer.setStyle(this.parentElement, 'position', this.previousParentPosition);
    }

    if (this.previousImageVisibility !== null) {
      this.renderer.setStyle(this.el.nativeElement, 'visibility', this.previousImageVisibility);
    }

    window.removeEventListener('resize', this.updateOverlayPosition);

    if (this.positionFrame !== null) {
      cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
    }

    this.overlay = null;
    this.overlayText = null;
    this.parentElement = null;
    this.previousParentPosition = null;
    this.previousImageVisibility = null;
  }
}
